import React, { useEffect, useMemo, useRef, useState } from "react";
import { GripVertical, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { addDays, addMonths, daysBetween, fmtYMD, fmtShortRange, parseYMD, startOfMonth } from "@/lib/plannerDate";

const ROW_H = 64;
const LABEL_W = 230;

/** One phase-tagged bar on the timeline. Body drag = move; the two 6px edge
 * handles resize start/end. A mousedown+mouseup with zero net movement is
 * treated as a click (opens the edit dialog) rather than a no-op drag. */
function Block({ block, phaseColor, left, width, isDragging, onMouseDownBody, onMouseDownEdge }) {
  return (
    <div
      className={`absolute top-2 bottom-2 rounded-full flex items-center px-2.5 text-[11px] font-semibold text-white cursor-grab select-none shadow-sm ${isDragging ? "opacity-80 ring-2 ring-white" : ""}`}
      style={{ left, width: Math.max(width, 18), background: phaseColor || "#94A3B8" }}
      onMouseDown={onMouseDownBody}
      data-testid={`planner-block-${block.id}`}
      title={`${block.title || "Untitled"} — ${fmtShortRange(block.start_date, block.due_date)}`}
    >
      <span className="truncate">{block.title || "Untitled"}</span>
      <span className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize" onMouseDown={onMouseDownEdge("resize-left")} />
      <span className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize" onMouseDown={onMouseDownEdge("resize-right")} />
    </div>
  );
}

export default function PlannerTimeline({
  projects, phases, buColors, orderMode, pxPerDay, windowStart, windowMonths,
  canEdit, onBlockMove, onBlockAdd, onBlockEdit, onBlockDelete, onStageToggle, onReorder,
}) {
  const phaseByKey = useMemo(() => Object.fromEntries((phases || []).map((p) => [p.key, p])), [phases]);
  const windowEnd = useMemo(() => addMonths(windowStart, windowMonths), [windowStart, windowMonths]);
  const totalDays = useMemo(() => daysBetween(windowStart, windowEnd), [windowStart, windowEnd]);
  const totalWidth = totalDays * pxPerDay;
  const today = useMemo(() => new Date(new Date().toDateString()), []);
  const todayLeft = daysBetween(windowStart, today) * pxPerDay;

  // ---- Drag/resize state (block move + resize) ---------------------------
  const dragRef = useRef(null);
  const pxPerDayRef = useRef(pxPerDay);
  pxPerDayRef.current = pxPerDay;
  const [dragBlockId, setDragBlockId] = useState(null);
  const [dragPreview, setDragPreview] = useState(null); // { start: Date, end: Date }
  const [editing, setEditing] = useState(null); // { block, projectId } | { adding: true, projectId, start } | null

  useEffect(() => {
    if (!dragBlockId) return;
    const onMove = (e) => {
      const d = dragRef.current;
      if (!d) return;
      const deltaDays = Math.round((e.clientX - d.startX) / pxPerDayRef.current);
      let ns = d.origStart, ne = d.origEnd;
      if (d.mode === "move") { ns = addDays(d.origStart, deltaDays); ne = addDays(d.origEnd, deltaDays); }
      else if (d.mode === "resize-left") { ns = addDays(d.origStart, deltaDays); if (ns > d.origEnd) ns = new Date(d.origEnd); }
      else if (d.mode === "resize-right") { ne = addDays(d.origEnd, deltaDays); if (ne < d.origStart) ne = new Date(d.origStart); }
      setDragPreview({ start: ns, end: ne });
    };
    const onUp = (e) => {
      const d = dragRef.current;
      dragRef.current = null;
      setDragBlockId(null);
      setDragPreview(null);
      if (!d) return;
      const deltaDays = Math.round((e.clientX - d.startX) / pxPerDayRef.current);
      if (deltaDays === 0) {
        setEditing({ block: d.block, projectId: d.projectId });
        return;
      }
      let ns = d.origStart, ne = d.origEnd;
      if (d.mode === "move") { ns = addDays(d.origStart, deltaDays); ne = addDays(d.origEnd, deltaDays); }
      else if (d.mode === "resize-left") { ns = addDays(d.origStart, deltaDays); if (ns > d.origEnd) ns = new Date(d.origEnd); }
      else if (d.mode === "resize-right") { ne = addDays(d.origEnd, deltaDays); if (ne < d.origStart) ne = new Date(d.origStart); }
      onBlockMove(d.block.id, fmtYMD(ns), fmtYMD(ne));
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
  }, [dragBlockId, onBlockMove]);

  const startDrag = (block, projectId, mode) => (e) => {
    if (!canEdit) return;
    e.stopPropagation();
    e.preventDefault();
    const origStart = parseYMD(block.start_date) || parseYMD(block.due_date) || today;
    const origEnd = parseYMD(block.due_date) || origStart;
    dragRef.current = { block, projectId, mode, startX: e.clientX, origStart, origEnd };
    setDragBlockId(block.id);
    setDragPreview({ start: origStart, end: origEnd });
  };

  // ---- Row manual reorder (native HTML5 DnD, same pattern as Leads board) --
  const [dragRowId, setDragRowId] = useState(null);
  const reorderEnabled = canEdit && orderMode === "manual";
  const onRowDrop = (targetId) => {
    if (!reorderEnabled || !dragRowId || dragRowId === targetId) { setDragRowId(null); return; }
    const ids = projects.map((p) => p.id);
    const from = ids.indexOf(dragRowId), to = ids.indexOf(targetId);
    if (from === -1 || to === -1) { setDragRowId(null); return; }
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    onReorder(ids.map((id, i) => ({ id, planner_order: i })));
    setDragRowId(null);
  };

  // ---- Month + week-tick header ------------------------------------------
  const months = useMemo(() => {
    const out = [];
    let cur = windowStart;
    let lastYear = null;
    while (cur < windowEnd) {
      const next = addMonths(cur, 1);
      const days = daysBetween(cur, next);
      out.push({ key: fmtYMD(cur), label: cur.toLocaleDateString([], { month: "short", ...(cur.getFullYear() !== lastYear ? { year: "numeric" } : {}) }), width: days * pxPerDay });
      lastYear = cur.getFullYear();
      cur = next;
    }
    return out;
  }, [windowStart, windowEnd, pxPerDay]);

  const weekTicks = useMemo(() => {
    const out = [];
    let cur = new Date(windowStart);
    while (cur.getDay() !== 1) cur = addDays(cur, 1); // first Monday on/after windowStart
    while (cur < windowEnd) {
      out.push({ key: fmtYMD(cur), left: daysBetween(windowStart, cur) * pxPerDay, label: cur.getDate() });
      cur = addDays(cur, 7);
    }
    return out;
  }, [windowStart, windowEnd, pxPerDay]);

  // Active rows visually grouped above Waitlist rows (matches reference).
  const activeRows = projects.filter((p) => (p.planner_stage || "active") !== "waitlist");
  const waitlistRows = projects.filter((p) => (p.planner_stage || "active") === "waitlist");
  const orderedRows = [...activeRows, ...waitlistRows];

  const openAddAt = (projectId, clickX) => {
    if (!canEdit) return;
    const dayOffset = Math.round(clickX / pxPerDay);
    const start = addDays(windowStart, dayOffset);
    setEditing({ adding: true, projectId, start, end: addDays(start, 6) });
  };

  return (
    <div className="card-flat overflow-hidden" data-testid="planner-timeline">
      <div className="flex text-xs text-slate-500 px-4 py-2 border-b border-slate-100 bg-slate-50/60">
        Click an empty spot on a row to add a block, name it anything and tag it to a phase. Drag blocks to move, pull an edge to change dates, click to edit. Switch a project between Active and Waitlist from the pill on its row.
      </div>
      <div className="flex">
        {/* Sticky label column */}
        <div className="shrink-0 border-r border-slate-200" style={{ width: LABEL_W }}>
          <div className="h-10 border-b border-slate-200 flex items-end px-3 pb-1 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Project and BU</div>
          {orderedRows.map((p) => (
            <div
              key={p.id}
              draggable={reorderEnabled}
              onDragStart={() => setDragRowId(p.id)}
              onDragOver={(e) => reorderEnabled && e.preventDefault()}
              onDrop={() => onRowDrop(p.id)}
              className={`flex items-center gap-1.5 px-3 border-b border-slate-100 ${(p.planner_stage || "active") === "active" ? "bg-emerald-50/50" : "bg-white"} ${dragRowId === p.id ? "opacity-40" : ""}`}
              style={{ height: ROW_H }}
              data-testid={`planner-row-label-${p.id}`}
            >
              {reorderEnabled && <GripVertical size={13} className="text-slate-300 shrink-0 cursor-grab" />}
              <div className="min-w-0 flex-1">
                <a href={`/projects/${p.id}`} className="text-[12px] font-semibold text-slate-800 hover:text-[#F54800] truncate block" data-testid={`planner-row-name-${p.id}`}>{p.name}</a>
                <div className="text-[10px] text-slate-400">{fmtShortRange(p.start_date, p.end_date)}</div>
                <div className="flex items-center gap-1 mt-0.5">
                  <Select value={p.planner_stage || "active"} onValueChange={(v) => canEdit && onStageToggle(p.id, v)} disabled={!canEdit}>
                    <SelectTrigger className="h-4 text-[9px] px-1.5 rounded-full border-slate-200 w-auto gap-0.5" data-testid={`planner-stage-${p.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="waitlist">Waitlist</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-[9px] px-1.5 rounded-full font-medium text-white shrink-0" style={{ background: buColors[p.business_unit] || "#94A3B8" }}>{p.business_unit || "—"}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Scrollable timeline */}
        <div className="flex-1 overflow-x-auto">
          <div style={{ width: totalWidth, position: "relative" }}>
            {/* Month + week header */}
            <div className="h-10 border-b border-slate-200 relative flex">
              {months.map((m) => (
                <div key={m.key} className="border-r border-slate-100 shrink-0 relative" style={{ width: m.width }}>
                  <div className="text-[11px] font-semibold text-slate-700 px-1.5 pt-1">{m.label}</div>
                </div>
              ))}
              {weekTicks.map((t) => (
                <div key={t.key} className="absolute top-6 text-[9px] text-slate-400" style={{ left: t.left + 3 }}>{t.label}</div>
              ))}
              {todayLeft >= 0 && todayLeft <= totalWidth && (
                <div className="absolute top-0 bottom-0 flex flex-col items-center" style={{ left: todayLeft }}>
                  <span className="text-[8px] font-bold text-[#F54800] bg-[#F54800]/10 px-1 rounded-sm mt-0.5">Today</span>
                </div>
              )}
            </div>

            {/* Rows */}
            <div className="relative">
              {/* Today vertical line spans all rows */}
              {todayLeft >= 0 && todayLeft <= totalWidth && (
                <div className="absolute top-0 bottom-0 w-px bg-[#F54800] z-10" style={{ left: todayLeft }} />
              )}
              {/* Month gridlines */}
              {months.reduce((acc, m) => {
                const x = acc.x + m.width;
                acc.lines.push(<div key={m.key} className="absolute top-0 bottom-0 w-px bg-slate-100" style={{ left: x }} />);
                acc.x = x;
                return acc;
              }, { x: 0, lines: [] }).lines}

              {orderedRows.map((p) => (
                <div
                  key={p.id}
                  className={`relative border-b border-slate-100 ${(p.planner_stage || "active") === "active" ? "bg-emerald-50/30" : "bg-white"}`}
                  style={{ height: ROW_H }}
                  onClick={(e) => {
                    if (e.target === e.currentTarget) openAddAt(p.id, e.nativeEvent.offsetX);
                  }}
                  data-testid={`planner-row-timeline-${p.id}`}
                >
                  {(p.blocks || []).map((b) => {
                    const isDragging = dragBlockId === b.id;
                    const s = isDragging ? dragPreview.start : (parseYMD(b.start_date) || parseYMD(b.due_date) || today);
                    const e = isDragging ? dragPreview.end : (parseYMD(b.due_date) || s);
                    const left = daysBetween(windowStart, s) * pxPerDay;
                    const width = (daysBetween(s, e) + 1) * pxPerDay;
                    return (
                      <Block
                        key={b.id}
                        block={b}
                        phaseColor={phaseByKey[b.phase]?.color}
                        left={left}
                        width={width}
                        isDragging={isDragging}
                        onMouseDownBody={startDrag(b, p.id, "move")}
                        onMouseDownEdge={(mode) => startDrag(b, p.id, mode)}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {editing && (
        <BlockDialog
          editing={editing}
          phases={phases}
          onClose={() => setEditing(null)}
          onSave={(patch) => {
            if (editing.adding) onBlockAdd(editing.projectId, patch);
            else onBlockEdit(editing.block.id, patch);
            setEditing(null);
          }}
          onDelete={editing.block ? () => { onBlockDelete(editing.block.id); setEditing(null); } : null}
        />
      )}
    </div>
  );
}

/** Shared dialog for both "add block" and "edit block" — title + phase +
 * start/due dates, with a Delete action when editing an existing block. */
function BlockDialog({ editing, phases, onClose, onSave, onDelete }) {
  const block = editing.block;
  const [title, setTitle] = useState(block?.title || "");
  const [phase, setPhase] = useState(block?.phase || phases?.[0]?.key || "strategy");
  const [start, setStart] = useState(block?.start_date || fmtYMDSafe(editing.start));
  const [end, setEnd] = useState(block?.due_date || fmtYMDSafe(editing.end));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-sm" data-testid="planner-block-dialog">
        <DialogHeader>
          <DialogTitle>{block ? "Edit block" : "New block"}</DialogTitle>
          <DialogDescription>{block ? "Update this phase block's name, phase, or dates." : "Name it anything and tag it to a phase."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="label-overline">Name</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Name it anything" className="rounded-sm mt-1" data-testid="planner-block-title-input" />
          </div>
          <div>
            <Label className="label-overline">Phase</Label>
            <Select value={phase} onValueChange={setPhase}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="planner-block-phase-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(phases || []).map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="label-overline">Start</Label>
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="rounded-sm mt-1" data-testid="planner-block-start-input" />
            </div>
            <div>
              <Label className="label-overline">End</Label>
              <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="rounded-sm mt-1" data-testid="planner-block-end-input" />
            </div>
          </div>
        </div>
        <DialogFooter className="flex items-center !justify-between sm:!justify-between">
          {onDelete ? (
            <Button variant="outline" size="sm" onClick={onDelete} className="rounded-sm border-red-200 text-red-600 hover:bg-red-50" data-testid="planner-block-delete">
              <Trash2 size={12} className="mr-1.5" /> Delete
            </Button>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose} className="rounded-sm">Cancel</Button>
            <Button
              size="sm"
              className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white"
              onClick={() => onSave({ title: title.trim() || "Untitled", phase, start_date: start, due_date: end })}
              data-testid="planner-block-save"
            >
              {block ? "Save" : "Add block"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function fmtYMDSafe(d) {
  if (!d) return "";
  return fmtYMD(d);
}
